<template>
  <v-dialog v-model="visible" width="650px" max-width="650px" no-click-animation persistent>
    <v-card>
      <v-card-title>Select a {{ fundingSourceLabel }}</v-card-title>
      <v-form :value="$v.form.$invalid">
        <v-card-text>
          <FundingSourceWithBudgetCode
            :useInputProps="true"
            :fsLabel="fundingSourceLabel"
            @input="setInvoiceFunding({ val: $event })"
            :addNoneFundingSource="isFirstInvoicePayment"
            :default-selected-funding-source="data"
            :existing-funding-sources="invoiceDetailsPayment?.invoicePayments"
          />
          <v-row>
            <v-col>
              <v-select
                data-cy="invoice-payment-split-method"
                v-if="!isSingleFundingSourceLimit"
                clear-icon
                :items="fundingSourceTypes"
                item-value="value"
                label="Split Method"
                v-model="$v.form.fundingSourceType.$model"
                v-bind="inputProps"
                @change="onFundingSourceTypeChange"
              ></v-select>
            </v-col>
            <template v-if="isPercentageMode">
              <v-col cols="3">
                <v-text-field
                  :max="100"
                  placeholder="0"
                  suffix="%"
                  type="number"
                  v-model="$v.form.percentage.$model"
                  v-bind="inputProps"
                  @change="onPercentageChange"
                ></v-text-field>
              </v-col>
            </template>
            <v-col :cols="isPercentageMode ? '3' : '5'">
              <v-text-field
                placeholder="0.00"
                prefix="$"
                type="number"
                v-model="$v.form.amount.$model"
                v-bind="inputProps"
                @change="onAmountChange"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="select" :disabled="!isFormValid">Select</v-btn>
          <v-btn color="default" @click="handleClose()">Cancel</v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>
<script>
import { mapGetters, mapActions } from 'vuex';
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import { handleErrors, inputProps, getBudgetCodeDescriptionWithLocation, percentageValue } from '@/util';
import { fundingSourceTypesMap } from '@/util/constants';
import FundingSourceWithBudgetCode from '@/components/FundingSource/FundingSourceWithBudgetCode.vue';

export default {
  mixins: [validationMixin],
  components: { FundingSourceWithBudgetCode },
  props: {
    visible: { type: Boolean, default: false },
    invoiceId: { type: Number, default: null },
    data: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      form: {
        fundingSource: null,
        fundingSourceType: '',
        amount: 0,
        percentage: 0,
        checkNumber: null,
        batchJournalNumber: null,
      },
      handleErrors,
      inputProps,
      required,
      budgetCodes: [],
      budgetCode: null,
      editedBudgetCode: null,
      fundingSourceTypesMap,
      oldFundingSourceId: null,
    };
  },
  validations() {
    return {
      form: {
        fundingSource: { required },
        fundingSourceType: {},
        amount: {},
        percentage: {},
      },
    };
  },
  computed: {
    ...mapGetters('invoice', [
      'invoiceDetailsPayment',
      'invoiceIsFetchingPayment',
      'selectedInvoice',
      'invoiceTotalCosts',
    ]),
    ...mapGetters('fundingSource', ['fundingSources', 'filterFundingSources', 'fundingSourcesById']),
    ...mapGetters('user', ['me']),
    ...mapGetters('invoicePayment', ['fundingSourceTypes']),
    ...mapGetters('config', ['budgetCodeConfig', 'tripRequestConfig']),
    ...mapGetters('location', ['locationsById']),
    isSingleFundingSourceLimit() {
      return parseInt(this.tripRequestConfig?.display?.fundingSourceLimit) === 1;
    },
    isFormValid() {
      if (
        !this.$v.form.fundingSource.$model ||
        this.selectedFundingSource?.type === this.fundingSourceTypesMap.editableBlank
      ) {
        return !this.$v.form.$invalid;
      }
      return !this.$v.form.$invalid && this.selectedBudgetCode.description;
    },
    isPercentageMode() {
      return this.$v.form.fundingSourceType.$model === 'percentage';
    },
    isFirstInvoicePayment() {
      const invoicePayments = this.invoiceDetailsPayment?.invoicePayments || [];
      return invoicePayments.length <= 1;
    },
    fundingSourceLabel() {
      return this.tripRequestConfig.labels?.addFundingSource || 'Funding source';
    },
    selectedFundingSource() {
      return this.fundingSourcesById?.[this.$v.form.fundingSource.$model] || null;
    },
    selectedBudgetCode() {
      if (this.budgetCode) return { description: this.budgetCode.text, id: this.budgetCode.value };

      const fundingSource = this.selectedFundingSource;
      if (!fundingSource) return '';

      const location = this.locationsById[this.selectedInvoice.locationId];
      if (!location) return fundingSource?.budgetCodeDescription || fundingSource?.budgetCode;

      const description = getBudgetCodeDescriptionWithLocation({
        budgetCodeConfig: this.budgetCodeConfig,
        budgetCodes: this.budgetCodes,
        fundingSource,
        locationCode: location?.code,
      });

      return { description, id: fundingSource.budgetCodeId };
    },
  },
  async created() {
    await this.getFundingSources();
  },
  methods: {
    ...mapActions('fundingSource', ['getFundingSources', 'saveFundingSource']),
    ...mapActions('invoice', ['saveInvoicePayment']),
    ...mapActions('budgetCode', ['getBudgetCodes']),
    ...mapActions('config', ['getConfig']),
    onFundingSourceTypeChange(value) {
      if (value === 'staff_cost') {
        if (+this.invoiceTotalCosts.staff === 0) return;
        this.$v.form.amount.$model = this.invoiceTotalCosts.staff;
      } else if (value === 'travel_cost') {
        if (+this.invoiceTotalCosts.travel === 0) return;
        this.$v.form.amount.$model = this.invoiceTotalCosts.travel;
      } else if (value === 'percentage') {
        this.$v.form.percentage.$model = this.data?.percentage || 100;
        this.onPercentageChange(this.$v.form.percentage.$model);
      } else {
        if (+this.$v.form.amount.$model) return;
        this.$v.form.amount.$model = this.data?.amount || 0.0;
      }
    },
    onPercentageChange(value) {
      let updatedValue = value;
      if (value > 100) {
        updatedValue = 100;
        this.$v.form.percentage.$model = updatedValue;
      }
      this.$v.form.amount.$model = percentageValue({ amount: updatedValue, total: this.invoiceTotalCosts.total });
    },
    onAmountChange(value) {
      if (this.isPercentageMode) {
        this.$v.form.percentage.$model = Math.min((value / this.invoiceTotalCosts.total) * 100, 100).toFixed(2);
      }
    },
    async select() {
      if (this.$v.form.$invalid) return;

      try {
        let budgetCode = null;

        await this.saveInvoicePayment({
          invoiceId: this.invoiceId,
          body: {
            id: this.form?.id || null,
            fundingSourceId: this.form.fundingSource,
            oldFundingSourceId: this.oldFundingSourceId,
            budgetCode: budgetCode || this.selectedBudgetCode.description,
            budgetCodeId: this.selectedBudgetCode.id || undefined,
            fundingSourceType: this.form.fundingSourceType,
            amount: this.form.amount,
            percentage: this.isPercentageMode ? this.form.percentage : null,
            checkNumber: this.form?.checkNumber || null,
            batchJournalNumber: this.form?.batchJournalNumber || null,
          },
        });
      } catch (error) {
        console.error('Saving Invoice Payment', error);
      }
      this.handleClose();
    },
    handleClose() {
      this.$emit('update:visible', false);
    },
    setInvoiceFunding({ val }) {
      this.form.fundingSource = val.fundingSourceId;
      this.budgetCode = { value: val.budgetCodeId, text: val.budgetCode };
    },
  },
  async mounted() {
    if (!this.invoiceDetailsPayment.invoicePayments.length) {
      this.$v.form.amount.$model = this.invoiceTotalCosts.total;
    }

    this.oldFundingSourceId = this.data?.fundingSourceId;
    this.budgetCodes = await this.getBudgetCodes();
    this.getConfig('tripRequestForm');
  },
  watch: {
    selectedBudgetCode(newVal) {
      if (this.selectedFundingSource?.type === this.fundingSourceTypesMap.editableBlank) {
        this.editedBudgetCode = newVal.description;
      }
    },
    data: {
      immediate: true,
      handler(newData) {
        if (Object.keys(newData).length > 0) {
          this.form = { ...newData, fundingSource: newData.fundingSourceId };
        }
      },
    },
  },
};
</script>
